// src/services/unified/systemModule.ts
import { BaseModule } from "./baseModule";

export interface SystemSetting {
  value: string;
  updated_at?: string;
}

export interface SystemSettings {
  [key: string]: SystemSetting;
}

export interface PublicSystemSettings {
  analytics_enabled?: SystemSetting;
  analytics_google_analytics_id?: SystemSetting;
  analytics_google_tag_manager_id?: SystemSetting;
  analytics_cookie_consent_required?: SystemSetting;
  application_name?: SystemSetting;
  tagline?: SystemSetting;
  company_description?: SystemSetting;
  company_logo?: SystemSetting;
  address?: SystemSetting;
  phone?: SystemSetting;
  email?: SystemSetting;
  social_linkedin?: SystemSetting;
  social_x?: SystemSetting;
  social_instagram?: SystemSetting;
  social_facebook?: SystemSetting;
}

export interface SettingUpdate {
  value: string;
  encrypt?: boolean;
}

export interface UpdateSystemSettingsRequest {
  settings: Record<string, SettingUpdate>;
}

export interface UpdateSystemSettingsResponse {
  message: string;
  updated_count: number;
  errors: string[];
}

/**
 * System Settings Module
 * Handles system configuration and public settings
 */
export class SystemModule extends BaseModule {
  /**
   * Get all system settings (admin only)
   */
  async getSystemSettings(): Promise<SystemSettings> {
    return this.get<SystemSettings>("/api/admin/settings");
  }

  /**
   * Update system settings (admin only)
   */
  async updateSystemSettings(
    request: UpdateSystemSettingsRequest,
  ): Promise<UpdateSystemSettingsResponse> {
    return this.put<UpdateSystemSettingsResponse>(
      "/api/admin/settings",
      request,
    );
  }

  /**
   * Get public system settings (no auth required)
   */
  async getPublicSettings(): Promise<PublicSystemSettings> {
    return this.get<PublicSystemSettings>("/api/settings/public", {
      skipAuth: true,
    });
  }

  /**
   * Get a specific public setting value
   */
  async getPublicSetting(key: string): Promise<string | null> {
    const settings = await this.getPublicSettings();
    const setting = settings[key as keyof PublicSystemSettings];
    return setting?.value || null;
  }

  /**
   * Get multiple public settings as a simple key-value map
   */
  async getPublicSettingsMap(): Promise<Record<string, string>> {
    const settings = await this.getPublicSettings();
    const map: Record<string, string> = {};

    if (settings) {
      Object.entries(settings).forEach(([key, setting]) => {
        if (setting?.value) {
          map[key] = setting.value;
        }
      });
    }

    return map;
  }

  /**
   * Upload company logo (admin only)
   */
  async uploadLogo(file: File): Promise<LogoUploadResponse> {
    const formData = new FormData();
    formData.append("logo", file);

    return this.post<LogoUploadResponse>("/api/admin/logo/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  /**
   * List all uploaded logos (admin only)
   */
  async listLogos(): Promise<LogoInfo[]> {
    return this.get<LogoInfo[]>("/api/admin/logos");
  }

  /**
   * Activate a specific logo (admin only)
   */
  async activateLogo(filename: string): Promise<{ message: string }> {
    return this.post<{ message: string }>("/api/admin/logo/activate", {
      filename,
    });
  }

  /**
   * Delete a specific logo file (admin only)
   */
  async deleteLogoFile(filename: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/api/admin/logo/${filename}`);
  }

  /**
   * List files in storage (admin only)
   */
  async listFiles(params?: ListFilesParams): Promise<FileItem[]> {
    const queryParams = new URLSearchParams();
    if (params?.path) queryParams.append("path", params.path);
    if (params?.search) queryParams.append("search", params.search);

    const query = queryParams.toString();
    const url = query ? `/api/admin/files?${query}` : "/api/admin/files";

    // Backend returns { files: [...], total: number }
    const response = await this.get<{ files: FileItem[]; total: number }>(url);
    return response.files || [];
  }

  /**
   * Get storage statistics (admin only)
   */
  async getStorageStats(): Promise<StorageStats> {
    return this.get<StorageStats>("/api/admin/files/stats");
  }

  /**
   * Delete a single file (admin only)
   */
  async deleteFile(path: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(
      `/api/admin/files/${encodeURIComponent(path)}`,
    );
  }

  /**
   * Delete multiple files (admin only)
   */
  async deleteFiles(
    request: DeleteFilesRequest,
  ): Promise<{ message: string; deleted_count: number }> {
    return this.post<{ message: string; deleted_count: number }>(
      "/api/admin/files/delete-bulk",
      request,
    );
  }
}

export interface LogoUploadResponse {
  message: string;
  logo_url: string;
}

export interface LogoInfo {
  filename: string;
  url: string;
  size: number;
  is_active: boolean;
  uploaded_at: number;
}

// File Manager Types
export interface FileItem {
  name: string;
  path: string;
  size: number;
  type: string;
  uploaded_at: string;
  uploaded_by?: string;
  url: string;
}

export interface StorageStats {
  total_size: number;
  file_count: number;
  quota?: number;
}

export interface ListFilesParams {
  path?: string;
  search?: string;
}

export interface DeleteFilesRequest {
  paths: string[];
}
